/**
 * The `context` of a VISITOR's turn — what `App.dispatch` sends beside the line.
 *
 * Built here rather than inline so the one key that differs per graph — a start
 * button's `starter` (asst-peterhof-guide#142) — is pinned by a test on the object
 * the run actually carries (`visitorContext.test.ts`), not on a helper next to it.
 * The spreads are `App.tsx`'s own, moved verbatim; a resume (`submitResume`) keeps
 * building its own context, which carries no tap.
 */

import { locationContext } from '../location/context';
import type { GeoMeta } from '../store/sessionStore';
import { tapContext } from '../a2ui/actions';
import { readsTripLink } from '../store/graphFamily';
import { readsStarter } from '../store/sessionStore';
import type { RunContext } from '../transport/api';

export type VisitorContextParts = {
  userId: string;
  assistantId: string;
  contextModel?: string;
  workerModel?: string;
  reasoningEffort?: RunContext['reasoning_effort'];
  workerReasoningEffort?: RunContext['worker_reasoning_effort'];
  temperature?: number;
  geo: { lat: number; lon: number } | null;
  geoMeta?: GeoMeta | null;
  agentGeo?: { lat: number; lon: number } | null;
  agentGeoSet?: boolean;
  locationResetAt?: number | null;
  tap: Record<string, unknown> | null;
};

export function visitorContext(p: VisitorContextParts): RunContext {
  return {
    user_id: p.userId,
    channel: 'web',
    // «По умолчанию (сервер)» sends no model at all — it honestly means
    // «as this instance is configured». `contextModel` already folds in
    // the orchestrator's own override (see `currentContextModel`).
    ...(p.contextModel ? { model: p.contextModel } : {}),
    // Absent means «as the deploy configures the second contour»
    // (`PW_MODEL` on the tandem, `PO_WORKER_MODEL` on the two
    // orchestrators), which is why it is omitted rather than sent as the
    // default. The field is `*_override` because each graph has a
    // `worker_model` of its own — the deploy's CLAIM for the header — and
    // the two must not be the same key: emptiness is what tells the graph
    // to leave the deploy's own setting alone
    // (`peterhof_tandem.context.Context.worker_model_override`,
    // `peterhof_orch.context.Context.worker_model_override`).
    ...(p.workerModel ? { worker_model_override: p.workerModel } : {}),
    ...(p.reasoningEffort ? { reasoning_effort: p.reasoningEffort } : {}),
    ...(p.workerReasoningEffort ? { worker_reasoning_effort: p.workerReasoningEffort } : {}),
    ...(p.temperature !== undefined ? { temperature: p.temperature } : {}),
    // Coordinates travel only when the visitor tapped for them.
    ...locationContext(p.assistantId, p.geo, p.geoMeta, p.agentGeo, p.agentGeoSet, p.locationResetAt),
    // Which card was tapped, for a graph that does not read it yet.
    // Declared on `Context` or it would be dropped in silence. A START
    // button instead says which questionnaire it opens — `starter`, only
    // to the graph that reads it (#142, `tapContext`).
    ...tapContext(p.tap, readsStarter(p.assistantId), readsTripLink(p.assistantId)),
  };
}
